<!--
 * @Author: LiZhiWei
 * @Date: 2026-01-22 14:08:24
 * @LastEditors: LiZhiWei
 * @LastEditTime: 2026-02-24 16:37:30
 * @Description: 解决方案详情页
-->
<template>
  <div v-if="solution" class="solution-page">
    <!-- Hero Section -->
    <section
      class="solution-hero w-full h-600px relative bg-cover bg-center bg-no-repeat lt-sm:h-auto lt-sm:py-160px"
    >
      <div
        class="landing-container flex flex-col items-center justify-center h-full text-center lt-sm:px-32px"
      >
        <h1
          class="hero-title pf-sc-semibold font-s-48px text-#000000 lt-sm:font-s-56px lt-sm:px-20px"
        >
          {{ solution.title }}
        </h1>
        <h2
          class="hero-subtitle pf-sc-semibold font-s-38px text-#000000 mb-16px lt-sm:font-s-38px lt-sm:px-20px"
        >
          {{ solution.subTitle }}
        </h2>
        <p class="hero-desc pf-sc-regular font-s-18px text-main/70 lt-sm:font-s-26px lt-sm:px-32px">
          {{ solution.description }}
        </p>
        <button
          class="hero-btn btn-primary pf-sc-semibold w-162px h-56px rounded-8px mt-36px font-s-18px text-white hover:opacity-80 transition-colors cursor-pointer lt-sm:w-224px lt-sm:h-71px lt-sm:rounded-8px lt-sm:font-s-28px bg-gradient-to-r from-[#779EFF] to-[#0A50FF] border-none outline-none"
          @click="openConsultation"
        >
          立即免费咨询
        </button>
      </div>
    </section>

    <!-- Core Pain Points -->
    <section
      ref="painPointsRef"
      class="section-container bg-white transition-all duration-1000 ease-out py-120px lt-sm:py-120px"
      :class="[isPainPointsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-50px']"
    >
      <div class="landing-container lt-sm:px-32px">
        <h2
          class="section-title pf-sc-semibold font-s-36px text-#000000 text-center lh-60px mb-40px lt-sm:font-s-48px lt-sm:mb-40px"
        >
          {{ solution.corePoints.title }}
        </h2>
        <div class="grid grid-cols-3 gap-25px lt-sm:grid-cols-1">
          <div
            v-for="(item, index) in solution.corePoints.points"
            :key="index"
            class="pain-card group bg-white rounded-16px p-40px border border-#E2E8F0/60 shadow-sm transition-all duration-300 text-center flex flex-col items-center lt-sm:rounded-16px lt-sm:p-40px"
          >
            <i
              :class="[item.icon, 'pain-icon wh-64px mb-16px lt-sm:wh-69px']"
              aria-hidden="true"
            ></i>
            <h3 class="pain-title pf-sc-semibold font-s-18px text-main mb-16px lt-sm:font-s-32px">
              {{ item.point }}
            </h3>
            <p
              class="pain-desc pf-sc-regular font-s-14px text-main/70 text-center lt-sm:font-s-24px"
            >
              {{ item.pointDesc }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Core Functions -->
    <section
      ref="functionsRef"
      class="section-container bg-#F6F8FD transition-all duration-1000 ease-out py-120px lt-sm:py-120px"
      :class="[isFunctionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-50px']"
    >
      <div class="landing-container lt-sm:px-32px">
        <h2
          class="section-title pf-sc-semibold font-s-36px text-#000000 text-center lh-60px mb-40px lt-sm:font-s-48px lt-sm:mb-40px"
        >
          {{ solution.coreFunctions.title }}
        </h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-24px mt-60px">
          <div
            v-for="(item, index) in solution.coreFunctions.function"
            :key="index"
            class="function-card bg-gradient-to-b from-[#E5E9F5] to-[#EFF2FB] rounded-16px p-40px transition-all duration-300 lt-sm:rounded-16px lt-sm:p-40px"
          >
            <div
              class="function-card-header flex items-start flex-col gap-12px mb-12px lt-sm:gap-24px lt-sm:mb-24px"
            >
              <i
                class="i-custom-function function-icon wh-44px lt-sm:wh-78px"
                aria-hidden="true"
              ></i>
              <h3 class="function-title pf-sc-semibold font-s-18px text-main lt-sm:font-s-32px">
                {{ item.name }}
              </h3>
            </div>
            <p
              class="function-desc pf-sc-regular font-s-14px text-main/70 mb-12px lh-24px lt-sm:font-s-24px lt-sm:lh-normal lt-sm:mb-24px"
            >
              {{ item.funcDesc }}
            </p>
            <div class="function-tags flex flex-wrap gap-26px lt-sm:gap-24px">
              <span
                v-for="(tag, tIndex) in item.funcPoints"
                :key="tIndex"
                class="function-tag pf-sc-medium flex items-center font-s-14px text-main gap-8px lt-sm:font-s-24px lt-sm:gap-8px"
              >
                <i class="i-custom-check-one wh-15px lt-sm:wh-24px" aria-hidden="true"></i>
                {{ tag }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Core Effects -->
    <section
      ref="effectsRef"
      class="section-container bg-white transition-all duration-1000 ease-out py-120px lt-sm:py-120px"
      :class="[isEffectsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-50px']"
    >
      <div class="landing-container">
        <h2
          class="section-title pf-sc-semibold font-s-36px text-#000000 text-center lh-60px mb-40px lt-sm:font-s-48px lt-sm:mb-40px"
        >
          {{ solution.coreEffects.title }}
        </h2>
        <div
          class="flex justify-between mt-80px gap-y-40px lt-sm:mt-72px lt-sm:grid lt-sm:grid-cols-2!"
        >
          <div
            v-for="(item, index) in solution.coreEffects.effect"
            :key="index"
            class="effect-item flex-1 text-center"
          >
            <div
              class="effect-percent d-din-pro-700-bold font-s-48px text-primary lt-sm:font-s-56px"
            >
              {{ item.percent }}
            </div>
            <p class="effect-name pf-sc-regular font-s-16px text-#384146">{{ item.name }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Typical Cases -->
    <!-- <section
      v-if="currentCase"
      ref="casesRef"
      class="section-container solution-case transition-all duration-1000 ease-out"
      :class="[isCasesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-50px']"
    >
      <div class="landing-container lt-sm:px-32px">
        <h2 class="section-title pf-sc-semibold">{{ solution.typicalCases.title }}</h2>
        <div class="mt-60px lt-sm:mt-48px">
          <solution-case-card
            :case-item="currentCase"
            :show-nav="(solution.typicalCases.cases?.length ?? 0) > 1"
            :is-first="activeCaseIndex === 0"
            :is-last="activeCaseIndex === (solution.typicalCases.cases?.length ?? 0) - 1"
            @prev="prevCase"
            @next="nextCase"
          />
        </div>
      </div>
    </section> -->

    <!-- CTA -->
    <section-cta />
  </div>
</template>

<script setup lang="ts">
  import { solutionPoints } from '@/constants/solutions'

  const route = useRoute()
  const { openConsultation } = useConsultation()

  const solution = computed(() => solutionPoints.find((item) => item.id === route.params.id))

  const painPointsRef = ref<HTMLElement | null>(null)
  const { isVisible: isPainPointsVisible } = useScrollReveal(painPointsRef)

  const functionsRef = ref<HTMLElement | null>(null)
  const { isVisible: isFunctionsVisible } = useScrollReveal(functionsRef)

  const effectsRef = ref<HTMLElement | null>(null)
  const { isVisible: isEffectsVisible } = useScrollReveal(effectsRef)

  const casesRef = ref<HTMLElement | null>(null)
  const { isVisible: isCasesVisible } = useScrollReveal(casesRef)

  const activeCaseIndex = ref(0)
  const currentCase = computed(() => {
    if (!solution.value?.typicalCases?.cases?.length) return null
    return solution.value.typicalCases.cases[activeCaseIndex.value]
  })

  const nextCase = () => {
    if (!solution.value?.typicalCases?.cases) return
    if (activeCaseIndex.value < solution.value.typicalCases.cases.length - 1) {
      activeCaseIndex.value++
    }
  }

  const prevCase = () => {
    if (activeCaseIndex.value > 0) {
      activeCaseIndex.value--
    }
  }

  useSeoMeta({
    title: () => `${solution.value?.title} - 绘家科技` || '解决方案',
    description: () =>
      solution.value?.description ||
      '绘家科技致力于为物业管理公司提供数字化转型解决方案，提升服务品质与业主满意度。',
    keywords: () =>
      `${solution.value?.title},智慧物业,解决方案,绘家科技,${solution.value?.corePoints.points.map((p) => p.point).join(',')}`,
    ogTitle: () => `${solution.value?.title || '解决方案'} - 绘家科技`,
    ogDescription: () =>
      solution.value?.description ||
      '绘家科技致力于为物业管理公司提供数字化转型解决方案，提升服务品质与业主满意度。',
    ogImage: '/logo.png',
  })
</script>

<style scoped>
  /* Hero */
  .solution-hero {
    background-image: url('~/assets/images/solution-bg.png');
  }

  @screen lt-sm {
    .solution-hero {
      background-image: url('~/assets/images/solution-mobile-bg.png');
    }
  }

  .solution-case {
    background-image: url('~/assets/images/case-bg.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }
</style>
